<template>
  <!-- MODAL -->
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-4xl sm:p-6"
            >
              <!-- Container de título e botão de fechar -->
              <div class="flex justify-between items-start">
                <h2 class="text-xl font-bold leading-9 tracking-tight text-gray-900">{{ title }}</h2>
                <button
                  @click="closeDialog"
                  type="button"
                  class="text-gray-400 hover:text-gray-600 -mt-4"
                >
                  <span class="sr-only">Fechar</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <!-- Conteúdo do Filtro -->
              <div class="mt-4">
                <form @submit.prevent="applyFilters" class="grid grid-cols-2 gap-4 mb-4 items-end">
                  <div v-for="field in fields" :key="field.name" class="relative">
                    <!-- Label visível -->
                    <label :for="field.name" class="block text-sm font-medium text-gray-700 mb-1">
                      {{ field.label }}
                    </label>

                    <!-- Input Normal -->
                    <template v-if="field.type === 'input'">
                      <input
                        :id="field.name"
                        v-model="filterValues[field.name]"
                        type="text"
                        class="block h-full w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:border-blue-400"
                        :placeholder="field.placeholder || 'Digite aqui...'"
                      />
                    </template>

                    <!-- Campo de Data -->
                    <template v-else-if="field.type === 'date'">
                      <input
                        :id="field.name"
                        v-model="filterValues[field.name]"
                        type="date"
                        class="block h-full w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 rounded-md"
                      />
                    </template>
                  </div>

                  <!-- Botão de Aplicar -->
                  <button
                    type="submit"
                    class="text-sm px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    Aplicar
                  </button>
                </form>

                <!-- Badges dos Filtros Aplicados -->
                <div class="flex flex-wrap gap-2">
                  <template v-for="(value, key) in appliedFilters" :key="key">
                    <span
                      v-if="key !== 'lista_de_agendamento_id'"
                      :key="key"
                      class="inline-flex items-center gap-x-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-300"
                    >
                      {{ key }}: {{ value }}
                      <button
                        type="button"
                        class="group relative -mr-1 size-3 rounded-sm hover:bg-gray-300"
                        @click="removeFilter(key)"
                      >
                        <span class="sr-only">Remover</span>
                        <svg viewBox="0 0 14 14" class="h-3 w-3 stroke-gray-500 group-hover:stroke-gray-800">
                          <path d="M4 4l6 6m0-6l-6 6" />
                        </svg>
                      </button>
                    </span>
                  </template>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
  
<script>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";

export default {
  name: "FilterModal",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    activeFilters: {
      type: Object,
      required: false,
      default: () => ({}),  // Definindo valor padrão como objeto vazio
    },
  },
  components: {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  },
  data() {
    return {
      filterValues: {},
      appliedFilters: {},
    };
  },
  created() {
    // Inicializa os valores de filtros com base nos campos definidos
    this.filterValues = this.fields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, {});
  },
  watch: {
    activeFilters: {
      deep: true,
      immediate: true,
      handler(newFilters) {
        // Limpa os valores anteriores
        this.filterValues = {};
        this.appliedFilters = {};

        // Percorre os filtros ativos
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            this.filterValues[key] = value;
            this.appliedFilters[key] = value;
          }
        });
      },
    },
  },
  methods: {
    closeDialog() {
      this.$emit("update-open", false);
    },
    applyFilters() {
      this.appliedFilters = { ...this.filterValues };
      for (const key in this.appliedFilters) {
        if (!this.appliedFilters[key]) {
          delete this.appliedFilters[key];
        }
      }
      this.$emit("apply-filters", this.appliedFilters);
    },
    removeFilter(key) {
      delete this.appliedFilters[key];
      this.filterValues[key] = "";
      this.$emit("apply-filters", this.appliedFilters);
    },
  },
};
</script>
